import React, { useEffect, useState } from "react";
import Header from "../../components/Header";
import { useAuthUser } from "../../contexts/useAuthUser";
import { firestore } from "../../services/firebase/index";
import { ActivityIndicator, Provider } from "react-native-paper";
import { Container } from "./style";
import { useNavigation } from "@react-navigation/native";
import Table from "./DataTable";
import {
    getDocs,
    collection,
    query,
    doc,
    where,
    setDoc,
    Timestamp,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getAuthCreationDates, getCountUsers, getUserContent, getUsers, getUsersScanBatch } from "./user-service";
import { getProducts } from "./products-service";
import { AlertBox } from "../../components/AlertBox";

const functions = getFunctions();

// Cache de sessão para as estatísticas de alunos (evita reler todos os docs a cada
// vez que a tela é aberta). É um full-scan dos docs de usuários (sem subcoleções).
// TTL: sem expiração, os cards ficavam desatualizados até um hard refresh
// (inclusive após cadastrar alunos ou trocar de conta na mesma aba).
let alunosStatsCache = null; // { data: {active, expired, noPurchase}, at: epoch-ms }
const STATS_CACHE_TTL_MS = 5 * 60 * 1000;
const statsCacheIsFresh = () =>
    !!alunosStatsCache && Date.now() - alunosStatsCache.at < STATS_CACHE_TTL_MS;

// Último resultado guardado no navegador. Numa base grande (dezenas de milhares
// de alunos) a varredura leva minutos, e o cache de sessão morre a cada reload —
// então a tela abria com "…" por muito tempo. Com isto o admin vê imediatamente
// os números da última apuração enquanto a nova roda por baixo.
const STATS_STORAGE_KEY = 'blix:alunosStats:v1';

const readStoredStats = () => {
    try {
        if (typeof localStorage === 'undefined') return null;
        const parsed = JSON.parse(localStorage.getItem(STATS_STORAGE_KEY) || 'null');
        return parsed && typeof parsed.active === 'number' ? parsed : null;
    } catch {
        return null;
    }
};

const writeStoredStats = (data) => {
    try {
        if (typeof localStorage === 'undefined') return;
        localStorage.setItem(STATS_STORAGE_KEY, JSON.stringify(data));
    } catch {
        // Sem localStorage (modo privado, quota) seguimos só com o cache de sessão.
    }
};

// Diretório de alunos (id/nome/e-mail + doc) para busca por NOME e autocomplete.
// É montado no MESMO full-scan das estatísticas (custo zero adicional) e cacheado
// por sessão — a busca por substring/autocomplete roda em memória, sem novas leituras.
let usersDirectoryCache = null; // { data: [{id, ...doc}], at: epoch-ms }
const directoryCacheIsFresh = () =>
    !!usersDirectoryCache && Date.now() - usersDirectoryCache.at < STATS_CACHE_TTL_MS;

export function Users() {
    const PAGE_SIZE = 10;

    const navigation = useNavigation();
    const [usersPage, setUserPage] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tableLoading, setTableLoading] = useState(true);
    const { user, authUser } = useAuthUser();
    const [filter, setFilter] = useState();
    const [products, setProducts] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [userReference, setUserReference] = useState([]);
    const [countUsers, setCountUsers] = useState();
    const [countFiltered, setCountFiltered] = useState();
    const [stats, setStats] = useState(alunosStatsCache?.data ?? readStoredStats());
    const [statsLoading, setStatsLoading] = useState(!statsCacheIsFresh());
    const [directoryReady, setDirectoryReady] = useState(directoryCacheIsFresh());
    const [isSearchResults, setIsSearchResults] = useState(false);
    const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
    const [showAlert, setShowAlert] = useState({
        visibleAlert: false,
        message: null,
    });

    // Calcula, em segundo plano, as estatísticas de acesso de TODOS os alunos.
    // Faz um full-scan dos docs de usuários (sem subcoleções) em lotes; resultado
    // cacheado por sessão e no navegador. Não bloqueia a tabela: os cards já
    // mostram a última apuração guardada e vão sendo atualizados a cada lote.
    const computeAllStats = async () => {
        if (statsCacheIsFresh() && directoryCacheIsFresh()) {
            setStats(alunosStatsCache.data);
            setStatsLoading(false);
            setDirectoryReady(true);
            return;
        }
        setStatsLoading(true);
        try {
            const now = new Date();
            const toDate = (v) => (v && typeof v.toDate === 'function' ? v.toDate() : (v instanceof Date ? v : null));
            let active = 0, expired = 0, noPurchase = 0;
            const directory = [];
            let lastVisible = null;
            // 2500 por lote: numa base de ~33 mil alunos são 13 idas ao servidor
            // em vez de 33. O volume de dados é o mesmo, o que cai é o número de
            // viagens — que é o que dominava o tempo total.
            const BATCH = 2500;

            while (true) {
                const snap = await getUsersScanBatch({ pageReference: lastVisible, pageSize: BATCH });
                if (!snap || snap.empty) break;

                snap.docs.forEach((d) => {
                    const u = d.data();
                    // Guarda o doc para busca por nome/e-mail e autocomplete (mesmo scan).
                    directory.push({ id: d.id, ...u });
                    const planOk = u.plan && u.plan !== "0" && !!u.planTitle;
                    const prodOk = Array.isArray(u.productIds) && u.productIds.some((p) => {
                        const e = toDate(p.expirationDate);
                        return p.productId && (!e || e > now);
                    });
                    const courseOk = Array.isArray(u.courses) && u.courses.some((c) => {
                        const e = toDate(c.dueDate);
                        return e && e > now;
                    });
                    const hasPurchase =
                        (u.plan && u.plan !== "0") ||
                        (Array.isArray(u.productIds) && u.productIds.length > 0) ||
                        (Array.isArray(u.courses) && u.courses.length > 0);

                    if (planOk || prodOk || courseOk) active++;
                    else if (hasPurchase) expired++;
                    else noPurchase++;
                });

                // Publica o parcial a cada lote: numa base grande a varredura
                // leva minutos, e antes os cards ficavam em "…" até o fim —
                // parecia travado. Agora os números sobem enquanto roda.
                setStats({ active, expired, noPurchase, scanned: directory.length, partial: true });

                if (snap.docs.length < BATCH) break;
                lastVisible = snap.docs[snap.docs.length - 1];
            }

            const result = { active, expired, noPurchase, scanned: directory.length };
            alunosStatsCache = { data: result, at: Date.now() };
            usersDirectoryCache = { data: directory, at: Date.now() };
            writeStoredStats(result);
            setStats(result);
            setDirectoryReady(true);
        } catch (error) {
            console.error("Erro ao calcular estatísticas de alunos:", error);
        } finally {
            setStatsLoading(false);
        }
    };

    // Garante o diretório carregado (dispara o scan se necessário) e o retorna.
    const ensureDirectory = async () => {
        if (directoryCacheIsFresh()) return usersDirectoryCache.data;
        await computeAllStats();
        return usersDirectoryCache?.data || [];
    };

    // Sugestões de autocomplete (nome OU e-mail contendo o termo), em memória.
    const getSuggestions = (term) => {
        const t = (term || '').trim().toLowerCase();
        if (t.length < 2 || !usersDirectoryCache) return [];
        const out = [];
        for (const u of usersDirectoryCache.data) {
            const name = (u.Nome_Completo || '').toLowerCase();
            const email = (u.Email || '').toLowerCase();
            if (name.includes(t) || email.includes(t)) {
                out.push({ id: u.id, name: u.Nome_Completo || '(sem nome)', email: u.Email || '' });
                if (out.length >= 8) break;
            }
        }
        return out;
    };

    // Conta os vídeos assistidos por aluno (1 leitura por aluno).
    // NÃO resolvemos os títulos aqui de propósito: a tabela só precisa da CONTAGEM,
    // e os títulos são buscados sob demanda na tela de Analytics ("Ver"). Resolver
    // títulos aqui gerava centenas de leituras sequenciais e travava a tela.
    const fetchContentsForUsers = async (users) => {
        const promises = users.map(async (user) => {
            try {
                const contentsSnapshot = await getUserContent(user.id);

                const watchedContents = contentsSnapshot.docs
                    .filter((contentDoc) => contentDoc.data().isWatched)
                    .map((contentDoc) => ({
                        categoryId: contentDoc.data().categoryId,
                        cardId: contentDoc.data().cardId,
                        videoId: contentDoc.id,
                        markedAsWatchedAt: contentDoc.data().markedAsWatchedAt
                    }))
                    .filter(content => content.categoryId && content.cardId && content.videoId); // Filtrando conteúdos com IDs válidos

                return { ...user, watchedContents, watchedVideosCount: watchedContents.length };
            } catch (error) {
                // Um aluno com dados de conteúdo inválidos não deve derrubar a página inteira.
                console.error(`Erro ao buscar conteúdos do aluno ${user.id}:`, error);
                return { ...user, watchedContents: [], watchedVideosCount: 0 };
            }
        });

        return Promise.all(promises);
    };

    // Vídeos assistidos + data REAL de criação da conta (Authentication) dos alunos
    // exibidos, em PARALELO para não somar latência à tela.
    // O `created_at` do doc é só um espelho: em docs legados ele guarda a data do
    // acesso manual/webhook que o carimbou por último, e a aluna cadastrada em
    // fev/2025 aparecia como nov/2025 (ticket de suporte). Sem a função publicada
    // ou fora do alcance, os alunos passam sem a data — a tabela cai no created_at.
    const enrichUsers = async (users) => {
        const [withContents, creationTimes] = await Promise.all([
            fetchContentsForUsers(users),
            getAuthCreationDates(users.map((u) => u.id)),
        ]);
        if (!Object.keys(creationTimes).length) return withContents;
        return withContents.map((u) =>
            creationTimes[u.id] ? { ...u, authCreatedAt: creationTimes[u.id] } : u
        );
    };

    // Sessão já resolvida no boot (ver AuthUserProvider); esta tela só é
    // alcançável por admin autenticado.
    useEffect(() => {
        if (!authUser) {
            setLoading(false);
            return;
        }

        fetchUsersPage(null, "", true);

        //count All Users
        getCountUsers({})
            .then(res => {
                const allUsersCount = res.data().count;
                setCountUsers(allUsersCount);
                setCountFiltered(allUsersCount);
            })
            .catch(err => console.error("Erro ao contar alunos:", err));

        //fetch products
        getProducts()
            .then((products) => setProducts(products))
            .catch(err => console.error("Erro ao carregar produtos:", err));

        //estatísticas de acesso (full-scan em segundo plano, cacheado por sessão)
        computeAllStats();
    }, [authUser]);

    const fetchAllUsers = async (batchSize, lastVisible) => {
        try {
            const response = await getUsers({
                pageReference: lastVisible,
                pageSize: batchSize,
                isNextPage: true,
            });

            if (response.docs.length === 0) {
                return { users: [], lastVisible: null };
            }

            const users = response.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
            }));

            const lastDoc = response.docs[response.docs.length - 1];

            const usersWithContents = await enrichUsers(users);

            return { users: usersWithContents, lastVisible: lastDoc };
        } catch (error) {
            console.error("Erro em fetchAllUsersHandler:", error);
            throw error;
        }
    };

    const fetchUsersPage = async (currentFilter, emailReference, isNextPage) => {
        setTableLoading(true);
        try {
            const response = await getUsers({
                pageReference: emailReference,
                pageSize: PAGE_SIZE,
                isNextPage: isNextPage,
                email: currentFilter,
            })

            const users = response.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
            }));
            const usersWithContents = await enrichUsers(users);

            setUserPage(usersWithContents);
            setUserReference(response.docs);
        } catch (error) {
            console.error("Error fetching users:", error);
        } finally {
            setTableLoading(false);
            setLoading(false);
        }
    }

    const handlePageChange = (page) => {
        // Em resultados de busca (nome/e-mail), a paginação é feita no cliente
        // pela própria tabela sobre os dados já carregados — sem ir ao servidor.
        if (isSearchResults) return;
        if (page > currentPage) {
            fetchUsersPage(filter, userReference[userReference.length - 1], true);
        } else {
            fetchUsersPage(filter, userReference[0], false);
        }

        setCurrentPage(page);
    };

    const handleSaveUser = async ({ email, selectedProducts, endDate, name, isSupportOnlyAdmin }) => {
        try {
            // E-mail sempre normalizado: casing/espaço divergente criava doc
            // "invisível" para a busca exata e recriações destrutivas. Docs
            // legados com outro casing caem no callable, que resolve pelo Auth.
            email = String(email || '').trim().toLowerCase();
            const usersCollection = collection(firestore, 'users');
            const q = query(usersCollection, where("Email", "==", email));
            const querySnapshot = await getDocs(q);

            if (!querySnapshot.empty) {
                const userId = querySnapshot.docs[0].id;
                if (isSupportOnlyAdmin) {
                    await setDoc(
                        doc(firestore, 'users', userId),
                        { isAdmin: true, adminRole: "supportOnly" },
                        { merge: true }
                    );
                } else {
                    const existingUserDoc = querySnapshot.docs[0];
                    const existingProductIds = existingUserDoc.data().productIds || [];
                    const existingCourses = existingUserDoc.data().courses || [];
                    // Acesso até o FIM do dia escolhido (23:59:59), como no backend
                    // (createUser) — à meia-noite o aluno perderia o dia final inteiro.
                    const endOfDay = new Date(endDate);
                    endOfDay.setHours(23, 59, 59, 999);
                    const dueTimestamp = Timestamp.fromDate(endOfDay);

                    const selected = selectedProducts.filter(p => p.selected);
                    // `source: 'manual'` protege o grant do painel dos webhooks.
                    const newSelectedProductIds = selected
                        .filter(p => p.marketplace !== "Curso" && p.marketplace !== "Plano")
                        .map(p => ({ productId: p.productId, expirationDate: dueTimestamp, source: 'manual' }));
                    const newSelectedCourses = selected
                        .filter(p => p.marketplace === "Curso")
                        .map(p => ({ productId: p.productId, dueDate: dueTimestamp }));
                    const selectedPlans = selected.filter(p => p.marketplace === "Plano");
                    const lastPlan = selectedPlans[selectedPlans.length - 1];
                    const planUpdate = lastPlan
                        ? { plan: lastPlan.productId, planTitle: lastPlan.name }
                        : {};

                    // Estende ou adiciona por produto. Antes, produto já presente
                    // era ignorado — re-conceder acesso a um aluno com grant
                    // vencido não fazia nada. Nunca REDUZ validade existente;
                    // sem data = perpétuo (vence qualquer data).
                    const isPurchaseEntry = p => !!(p?.subGuruId || p?.hotmartSubscriptionCode || p?.productGuruId || p?.transactionIdentifier);
                    const toJsDate = d => (d instanceof Date ? d : d?.toDate?.() ?? null);
                    const widerDate = (a, b) => {
                        const dateA = toJsDate(a);
                        const dateB = toJsDate(b);
                        if (!dateA || !dateB) return null;
                        return dateA > dateB ? a : b;
                    };
                    const combinedProductIds = [...existingProductIds];
                    newSelectedProductIds.forEach(newProduct => {
                        const idx = combinedProductIds.findIndex(
                            p => p.productId === newProduct.productId && !isPurchaseEntry(p)
                        );
                        if (idx === -1) {
                            combinedProductIds.push(newProduct);
                        } else {
                            combinedProductIds[idx] = {
                                ...combinedProductIds[idx],
                                source: 'manual',
                                expirationDate: widerDate(combinedProductIds[idx].expirationDate, newProduct.expirationDate),
                            };
                        }
                    });
                    const combinedCourses = [...existingCourses];
                    newSelectedCourses.forEach(newCourse => {
                        const idx = combinedCourses.findIndex(c => c.productId === newCourse.productId);
                        if (idx === -1) {
                            combinedCourses.push(newCourse);
                        } else {
                            combinedCourses[idx] = {
                                ...combinedCourses[idx],
                                dueDate: widerDate(combinedCourses[idx].dueDate, newCourse.dueDate),
                            };
                        }
                    });

                    await setDoc(
                        doc(firestore, 'users', userId),
                        { productIds: combinedProductIds, courses: combinedCourses, ...planUpdate },
                        { merge: true }
                    );
                }
            } else {
                const createUserFunction = httpsCallable(functions, "createUser");
                const createPayload = { email, selectedProducts, endDate, name };
                if (isSupportOnlyAdmin) {
                    createPayload.isSupportOnlyAdmin = true;
                }
                await createUserFunction(createPayload);
            }
            // Acessos mudaram: invalida o cache dos cards e recalcula em segundo plano.
            alunosStatsCache = null;
            computeAllStats();
            setShowAlert({
                visibleAlert: true,
                message: isSupportOnlyAdmin ? 'Administrador de suporte salvo com sucesso!' : 'Aluno salvo com sucesso!',
            });
        } catch (error) {
            console.error('Erro ao salvar usuário:', error);
            if (error.code === 'failed-precondition') {
                setShowAlert({
                    visibleAlert: true,
                    message: 'Erro: Token de aluno não configurado. Contate o administrador.',
                });
            } else {
                setShowAlert({
                    visibleAlert: true,
                    message: 'Erro ao salvar o aluno. Tente novamente.',
                });
            }
        }
    };

    const handleFilter = async (text) => {
        // Remove espaços nas bordas (um espaço acidental zerava a busca antes).
        text = (text || '').trim();
        setFilter(text);
        setResetPaginationToggle(v => !v);

        // Sem termo: volta à listagem paginada padrão (server-side, barato).
        if (!text) {
            setIsSearchResults(false);
            fetchUsersPage('', null, true);
            getCountUsers({})
                .then(res => setCountFiltered(res.data().count))
                .catch(err => console.error('Erro ao contar alunos:', err));
            return;
        }

        // Com termo: busca por NOME ou E-MAIL (substring) sobre o diretório em
        // cache — em memória, sem novas leituras. Cobre "ana" → todas as Anas.
        setIsSearchResults(true);
        setTableLoading(true);
        try {
            const dir = await ensureDirectory();
            const term = text.toLowerCase();
            const matches = dir.filter((u) =>
                (u.Nome_Completo || '').toLowerCase().includes(term) ||
                (u.Email || '').toLowerCase().includes(term)
            );
            setCountFiltered(matches.length);
            // Limita para não disparar muitas leituras de "vídeos assistidos";
            // o admin refina o termo se precisar de alguém específico.
            const capped = matches.slice(0, 50);
            const withContents = await enrichUsers(capped);
            setUserPage(withContents);
        } catch (error) {
            console.error('Erro na busca de alunos:', error);
        } finally {
            setTableLoading(false);
        }
    };

    const hideAlert = () => {
        setShowAlert({
            visibleAlert: false,
            message: null,
        });
    };


    return (
        <Provider>
            <Container>
                <Header navigation={navigation} user={user} isAdminPage />
                <Table
                    tableData={usersPage}
                    user={user}
                    products={products}
                    totalCount={countUsers}
                    totalFiltered={countFiltered}
                    stats={stats}
                    statsLoading={statsLoading}
                    loading={tableLoading}
                    handleSaveUser={handleSaveUser}
                    handlePageChange={handlePageChange}
                    handleFilter={handleFilter}
                    getSuggestions={getSuggestions}
                    isSearchResults={isSearchResults}
                    directoryReady={directoryReady}
                    fetchAllUsersHandler={fetchAllUsers}
                    resetPaginationToggle={resetPaginationToggle}
                />
                <AlertBox variant="admin"
                    message={showAlert.message}
                    visible={showAlert.visibleAlert}
                    onClose={hideAlert}
                    leftButton={'OK'}
                    leftButtonFunction={() => setShowAlert(false)}
                />
            </Container>
        </Provider>
    );
}